import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, FileText, Flame, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { api } from "../lib/api";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, tint }) {
    return (
        <div className="rounded-3xl border border-ink-300/30 bg-white p-5">
            <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${tint}`}>
                <Icon className="h-5 w-5" />
            </div>
            <div className="mt-3 font-display text-3xl font-bold">{value}</div>
            <div className="text-xs text-ink-500">{label}</div>
        </div>
    );
}

export default function AdminDashboardPage() {
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [filter, setFilter] = useState({ status: "all", package: "all", high: "all" });
    const [loading, setLoading] = useState(true);

    const loadData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter.status !== "all") params.status = filter.status;
            if (filter.package !== "all") params.package = filter.package;
            if (filter.high !== "all") params.high_intent = filter.high === "yes";

            const [s, l] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/leads", { params }),
            ]);
            setStats(s.data);
            setLeads(l.data || []);
        } catch (e) {
            toast.error("Admin-Daten konnten nicht geladen werden.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    return (
        <div className="min-h-[80vh] bg-brand-50/10 py-10">
            <div className="container max-w-6xl">
                <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">Admin</div>
                        <h1 className="mt-1 font-display text-3xl font-bold sm:text-4xl">Beraterbereich</h1>
                    </div>
                    <Button onClick={loadData} variant="outline" className="rounded-full" data-testid="admin-refresh">
                        Aktualisieren
                    </Button>
                </div>

                {/* Stats */}
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    <StatCard icon={Users} label="Nutzer" value={stats?.total_users ?? "–"} tint="bg-brand-50 text-brand-600" />
                    <StatCard icon={FileText} label="Fälle" value={stats?.total_cases ?? "–"} tint="bg-brand-50 text-brand-600" />
                    <StatCard icon={FileText} label="Leads gesamt" value={stats?.total_leads ?? "–"} tint="bg-brand-50 text-brand-600" />
                    <StatCard icon={Star} label="Neue Leads" value={stats?.new_leads ?? "–"} tint="bg-amber-50 text-amber-700" />
                    <StatCard icon={Flame} label="High-Intent" value={stats?.high_intent_leads ?? "–"} tint="bg-rose-50 text-rose-600" />
                </div>

                {/* Filters */}
                <div className="mt-8 grid gap-3 rounded-2xl border border-ink-300/30 bg-white p-4 sm:grid-cols-4">
                    <div>
                        <div className="mb-1 text-xs text-ink-500">Status</div>
                        <Select value={filter.status} onValueChange={(v) => setFilter((f) => ({ ...f, status: v }))}>
                            <SelectTrigger className="h-11 rounded-xl" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Alle</SelectItem>
                                <SelectItem value="new">Neu</SelectItem>
                                <SelectItem value="contacted">Kontaktiert</SelectItem>
                                <SelectItem value="qualified">Qualifiziert</SelectItem>
                                <SelectItem value="won">Abgeschlossen</SelectItem>
                                <SelectItem value="lost">Verloren</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-ink-500">Paket</div>
                        <Select value={filter.package} onValueChange={(v) => setFilter((f) => ({ ...f, package: v }))}>
                            <SelectTrigger className="h-11 rounded-xl" data-testid="admin-filter-package"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Alle</SelectItem>
                                <SelectItem value="Bronze">Bronze</SelectItem>
                                <SelectItem value="Silber">Silber</SelectItem>
                                <SelectItem value="Gold">Gold</SelectItem>
                                <SelectItem value="Unsicher">Unsicher</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <div className="mb-1 text-xs text-ink-500">High Intent</div>
                        <Select value={filter.high} onValueChange={(v) => setFilter((f) => ({ ...f, high: v }))}>
                            <SelectTrigger className="h-11 rounded-xl" data-testid="admin-filter-high"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Alle</SelectItem>
                                <SelectItem value="yes">Ja</SelectItem>
                                <SelectItem value="no">Nein</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Leads */}
                <div className="mt-6 overflow-hidden rounded-3xl border border-ink-300/30 bg-white">
                    <div className="grid grid-cols-12 border-b border-ink-300/30 bg-brand-50/40 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-700">
                        <div className="col-span-3">Name / Mail</div>
                        <div className="col-span-2">Paket</div>
                        <div className="col-span-2">Status</div>
                        <div className="col-span-2">Score</div>
                        <div className="col-span-2">Datum</div>
                        <div className="col-span-1 text-right">Aktion</div>
                    </div>
                    {loading && <div className="p-8 text-center text-sm text-ink-500">Lade…</div>}
                    {!loading && leads.length === 0 && (
                        <div className="p-8 text-center text-sm text-ink-500">Keine Leads gefunden.</div>
                    )}
                    {!loading &&
                        leads.map((l) => (
                            <div
                                key={l.lead_id}
                                className="grid grid-cols-12 items-center border-b border-ink-300/20 px-5 py-4 text-sm"
                                data-testid={`admin-lead-row-${l.lead_id}`}
                            >
                                <div className="col-span-3">
                                    <div className="font-medium text-ink-900">
                                        {l.form_data?.first_name} {l.form_data?.last_name}
                                    </div>
                                    <div className="text-xs text-ink-500">{l.form_data?.email}</div>
                                </div>
                                <div className="col-span-2">
                                    <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
                                        {l.package_interest}
                                    </span>
                                </div>
                                <div className="col-span-2">
                                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                                        l.status === "new"
                                            ? "bg-amber-100 text-amber-800"
                                            : l.status === "won"
                                            ? "bg-emerald-100 text-emerald-800"
                                            : "bg-ink-100 bg-muted"
                                    }`}>
                                        {l.status}
                                    </span>
                                </div>
                                <div className="col-span-2 flex items-center gap-2">
                                    <span className="font-mono">{l.lead_score}</span>
                                    {l.high_intent && <Flame className="h-4 w-4 text-rose-500" aria-label="High intent" />}
                                </div>
                                <div className="col-span-2 text-xs text-ink-500">
                                    {new Date(l.created_at).toLocaleString("de-DE")}
                                </div>
                                <div className="col-span-1 text-right">
                                    <Link to={`/admin/leads/${l.lead_id}`} data-testid={`admin-lead-open-${l.lead_id}`}>
                                        <Button variant="ghost" size="sm" className="rounded-full">
                                            Öffnen <ArrowRight className="ml-1 h-3 w-3" />
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );
}
